import axios from 'axios';
import { clearSession, setSession } from '../contexts/AuthContext.tsx';
import { ep } from './endpoints.ts';
import { navigate } from './navigator.ts';
import { getTenantId } from './tenantHeader.ts';

const shouldAttachTenantHeader = (url?: string) => {
  if (!url) return false;
  return !url.startsWith('/api/v1/super/')
    && !url.startsWith('/api/v1/auth/')
    && !url.startsWith('/api/v1/tenants');
};


const client = axios.create({
  // baseURL left blank so ep already contains /api/v1
  baseURL: '',
  withCredentials: true,
});

client.interceptors.request.use((cfg) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    cfg.headers = cfg.headers || {};
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    cfg.headers.Authorization = `Bearer ${token}`;
  }
  // Inject superadmin tenant override header if available
  const tid = getTenantId();
  if (tid && shouldAttachTenantHeader(cfg.url)) {
    cfg.headers = cfg.headers || {};
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    cfg.headers['X-Tenant-Id'] = String(tid);
  }
  return cfg;
});

client.interceptors.response.use(
  (res) => {
    // Log request ID for debugging
    const requestId = res.headers['x-request-id'];
    if (requestId) {
      console.debug(`[API Response] Request ID: ${requestId}, Status: ${res.status}, URL: ${res.config.url}`);
    }
    return res;
  },
  async (err) => {
    const { response, config } = err;
    
    // Log request ID for error tracking
    if (response) {
      const requestId = response.headers['x-request-id'];
      if (requestId) {
        console.error(`[API Error] Request ID: ${requestId}, Status: ${response.status}, URL: ${config?.url}`, response.data);
      }
    }
    
    const onLoginPageNow = window.location.pathname.startsWith('/login');
    if (onLoginPageNow || !response || response.status !== 401) {
      return Promise.reject(err);
    }

    // Already retried once → logout
    // eslint-disable-next-line no-underscore-dangle
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if ((config as any).__isRetry) {
      clearSession();
      navigate('/login');
      return Promise.reject(err);
    }

    try {
      // eslint-disable-next-line no-underscore-dangle
      // cast to any to allow custom marker
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const r = await client.post(ep.authRefresh, {}, { __isRetry: true } as any);
      const t = r.data?.accessToken || r.data?.access_token || r.data?.token; setSession(t);
      // mark original request so we don't loop
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (config as any).__isRetry = true;
      return client(config);
    } catch (e) {
      clearSession();
      navigate('/login');
      return Promise.reject(e);
    }
  }
);

export default client;
